import React, { useMemo, useRef, useState } from 'react';
import { Button, Form, Typography } from 'antd';
import { find, get } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { Icoupon } from '@/redux/reducers/guest';
import {
  CouponStatus,
  CouponTabStatus,
  // COUPON_SUB_TYPE_MAP,
  // DATE_FORMAT,
  CouponRuleCodeMap,
  CouponUseShopRange,
  CouponUseShopRangeMap,
  DATE_FORMAT,
} from '@/config';
import {
  CouponDescriptionGetter,
  isDeductByValue,
} from '@/components/PresentedCouponDrawer/helper';
import { parseFen2Currency, parseFen2Yuan } from '@/utils/number';
import { renderIntlLabel } from '@/utils';
import { getTimeByUtcOffset } from '@/utils/intl';
import styles from './index.module.scss';

const { Text } = Typography;

interface CouponItemProps {
  isCanSelect?: boolean; // 是否可选(区分券列表、券选择)
  coupon?: Partial<Icoupon>; // 券详情
  couponTabStatus?: CouponTabStatus; // 券的四种聚合状态(券列表)
  checked?: boolean; // 是否选中
  disabled?: boolean; // 是否不可选
  onClick?: (couponNo: string, value?: any) => void; // 点击回调
  onShowEditModal?: (...args) => void;
}

const CouponItem: React.FC<CouponItemProps> = ({
  isCanSelect = true,
  coupon = {},
  couponTabStatus,
  checked,
  disabled,
  onClick,
  onShowEditModal,
}) => {
  const finalCoupon = useMemo(() => {
    const tempObj = { ...coupon };
    if (!coupon?.ruleSet?.ruleList) {
      let conditionList = [];
      let resultList = [];
      const actionRulesItem = tempObj?.ruleSet?.actionRules?.find(
        (actionItem) => actionItem.action === 'USING',
      );
      if (
        Array.isArray(actionRulesItem?.conditionRules) &&
        actionRulesItem?.conditionRules.length
      ) {
        conditionList = actionRulesItem?.conditionRules;
      }
      if (Array.isArray(actionRulesItem?.resultRules) && actionRulesItem?.resultRules.length) {
        resultList = actionRulesItem.resultRules;
      }
      tempObj.ruleSet.ruleList = conditionList?.concat(resultList);
    }
    return tempObj;
  }, [coupon]);

  const [isEllipsis, setIsEllipsis] = useState<boolean>(true);
  const [showMorePop, setShowMorePop] = useState<boolean>(false);
  const couponItemContainerRef = useRef<HTMLDivElement>();
  const detailAbsolutePositionRef = useRef<HTMLDivElement>();

  const handleClickItem = () => {
    onClick?.(finalCoupon.couponNo, finalCoupon);
  };

  // const handleEllipsis = (ellipsis) => {
  //   setIsEllipsis(ellipsis);
  // };

  const handleTogglePop = () => {
    setShowMorePop(!showMorePop);
  };

  const handleClosePop = () => {
    setShowMorePop(false);
  };

  const resizeParentEleHeight = (currentOpened) => {
    const containerParentHeight = couponItemContainerRef.current.parentElement.offsetHeight;
    let height = containerParentHeight;
    if (currentOpened) {
      const moreDetailHeight = detailAbsolutePositionRef.current.offsetHeight;
      height = containerParentHeight + moreDetailHeight;
    } else {
      height = couponItemContainerRef.current.offsetHeight;
    }
    couponItemContainerRef.current.parentElement.style.height = `${height}px`;
  };

  // useLayoutEffect(() => {
  // resizeParentEleHeight(showMorePop);
  // }, [showMorePop]);

  const renderCouponFaceValue = () => {
    if (isDeductByValue(finalCoupon.couponSubType)) {
      return (
        <>
          <div className={styles.money}>{parseFen2Currency(finalCoupon.couponFaceValue)}</div>
        </>
      );
    }
    return (
      <>
        <div className={styles.money}>
          {(Number(finalCoupon.couponFaceValue) / 1000).toFixed(1)}
          <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
            {renderIntlLabel('guest_coupon_label_discount')}
          </span>
        </div>
      </>
    );
  };

  const renderServiceThreshold = () => {
    const ruleList = get(finalCoupon, 'ruleSet.ruleList');
    // 使用门槛
    const limitVal = parseFen2Yuan(
      get(find(ruleList, { code: CouponRuleCodeMap.deductStartCode }), 'valueList.0'),
    );
    const isNoLimit = !limitVal;
    if (isDeductByValue(finalCoupon.couponSubType)) {
      // 满减
      // const deductVal = parseFen2Yuan(
      //   get(find(ruleList, { code: CouponRuleCodeMap.deductAmountCode }), 'valueList.0'),
      // );
      return isNoLimit ? (
        ''
      ) : (
        <div>{renderIntlLabel('guest_msg_reach_avalible_yuan', { limitVal })}</div>
      );
    }
    // 满折
    const percentRule = find(ruleList, { code: CouponRuleCodeMap.deductPercentCode });
    // const percentVal = new BigNumber(get(percentRule, 'valueList.0')).dividedBy(1000).toNumber();
    const maxDeductVal = parseFen2Yuan(get(percentRule, 'valueList.1'));
    return isNoLimit ? (
      <div>{renderIntlLabel('guest_msg_max_deduction', { maxDeductVal })}</div>
    ) : (
      <>
        <div>{renderIntlLabel('guest_msg_reach_avalible_yuan')}</div>
        <div>{renderIntlLabel('guest_msg_max_deduction', {})}</div>
      </>
    );
  };

  return (
    <>
      <div
        className={classNames(styles.container, {
          [styles.unable]:
            (isCanSelect && disabled) ||
            // 线下不可选
            (!isCanSelect && finalCoupon.walkinDisabled) ||
            (!isCanSelect && couponTabStatus === CouponTabStatus.CannotUse),
          // 只用于券列表展示
          [styles.haveUsed]: !isCanSelect && couponTabStatus === CouponTabStatus.Used,
          [styles.overdue]: !isCanSelect && couponTabStatus === CouponTabStatus.Expired,
          // 只用于券列表选择
          [styles.selected]: checked,
        })}
        ref={couponItemContainerRef}
      >
        <div
          className={classNames(styles.content, {
            [styles.lock]:
              // 锁标志
              disabled || finalCoupon.status === CouponStatus.Locked,
          })}
          onClick={handleClickItem}
        >
          <div className="flex" style={{ flex: 1, alignItems: 'flex-start' }}>
            <div className={styles.img}>&nbsp;</div>
            <div className={styles.info}>
              <div>
                <span className={styles.tag}>
                  {CouponDescriptionGetter.couponTypeDesc(finalCoupon)}
                </span>
                <Text
                  className={styles.couponName}
                  style={{ width: 140 }}
                  ellipsis={{ tooltip: finalCoupon.couponName }}
                >
                  {finalCoupon.couponName}
                </Text>
              </div>
              <div>
                {renderIntlLabel('guest_coupon_label_effective')}：
                {finalCoupon?.startDate
                  ? `${getTimeByUtcOffset(finalCoupon?.startDate, DATE_FORMAT)}~`
                  : ''}
                {finalCoupon?.endDate ? getTimeByUtcOffset(finalCoupon?.endDate, DATE_FORMAT) : ''}
              </div>
              <div>
                {renderIntlLabel('guest_coupon_label_avalible_effective_range')}：
                {CouponDescriptionGetter.useTimeDesc(finalCoupon)}
              </div>
              <div>
                {renderIntlLabel('guest_coupon_label_avalible_store_range')}：
                {finalCoupon?.couponStoreRangeType === CouponUseShopRange.SingleShop
                  ? `${renderIntlLabel('guest_coupon_label_only')}${
                      finalCoupon?.bindCommonCode || ''
                    }${finalCoupon?.bindCommonName || ''}${renderIntlLabel(
                      'guest_coupon_label_keyong',
                    )}`
                  : CouponUseShopRangeMap[finalCoupon?.couponStoreRangeType]}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            {renderCouponFaceValue()}
            {renderServiceThreshold()}
          </div>
        </div>

        <div className={`${styles.moreDetail} flex-start`}>
          <div style={{ minWidth: '350px', marginRight: 'auto' }} onClick={handleClickItem}>
            {/* <Paragraph
              ellipsis={{
                rows: 1,
                expandable: false,
                onEllipsis: handleEllipsis,
              }}
            >
              {finalCoupon.couponDescription}
            </Paragraph> */}
          </div>

          {/* 未锁定、本店可用/不可用 */}
          {!disabled &&
            coupon.status !== CouponStatus.Locked &&
            (couponTabStatus === CouponTabStatus.CanUse ||
              couponTabStatus === CouponTabStatus.CannotUse) && (
              <div>
                <Button
                  type="link"
                  onClick={() => onShowEditModal(coupon)}
                  style={{ marginRight: '16px' }}
                >
                  {renderIntlLabel('order_action_edit')}
                </Button>
              </div>
            )}
          {isEllipsis && (
            <div className={styles.moreContainer}>
              <Button type="link" onClick={handleTogglePop} onBlur={handleClosePop}>
                {renderIntlLabel('order_action_more')}
              </Button>
              {showMorePop && (
                <div className={styles.showMorePop} ref={detailAbsolutePositionRef}>
                  <Form labelCol={{ span: 6 }} labelAlign="left" wrapperCol={{ span: 18 }}>
                    <Form.Item label={renderIntlLabel('guest_coupon_label_avalible_occtype')}>
                      {CouponDescriptionGetter.useOccTypeDesc(finalCoupon)}
                    </Form.Item>
                    <Form.Item label={renderIntlLabel('guest_coupon_label_hexiao_channel_limit')}>
                      {CouponDescriptionGetter.useChannelDesc(finalCoupon)}
                    </Form.Item>
                    <Form.Item label={renderIntlLabel('guest_coupon_label_use_condition')}>
                      {finalCoupon?.couponCondition}
                    </Form.Item>
                    <Form.Item
                      label={renderIntlLabel('guest_coupon_label_use_explain')}
                      className={styles.preWrapText}
                    >
                      {finalCoupon?.couponDescription}
                    </Form.Item>
                  </Form>
                </div>
              )}
            </div>
            // <Popconfirm
            //   title="券规则1234券规则1234券规则1234券规则1234券规则12券规则1234券规则1234券规则1234券规则1234券规则12规则1234券规则1234券规则12"
            //   icon=""
            //   placement="bottomRight"
            //   overlayClassName="editPopConfirm"
            //   getPopupContainer={() => couponItemContainerRef.current}
            //   overlayStyle={{ width: '400px' }}
            // >
            // </Popconfirm>
          )}
        </div>
      </div>
    </>
  );
};

export default CouponItem;
